/**
 * Fishbone (Ishikawa) Diagram Editor Component
 * Supports 6M and SHELL models with AI-assisted cause identification
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  Plus, X, Sparkles, Loader2, Brain, AlertTriangle 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fishbone category colors
const FISHBONE_COLORS = {
  man: 'bg-blue-500/20 border-blue-500/50 text-blue-300',
  machine: 'bg-gray-500/20 border-gray-500/50 text-gray-300',
  material: 'bg-orange-500/20 border-orange-500/50 text-orange-300',
  method: 'bg-purple-500/20 border-purple-500/50 text-purple-300',
  measurement: 'bg-green-500/20 border-green-500/50 text-green-300',
  mother_nature: 'bg-sky-500/20 border-sky-500/50 text-sky-300',
  software: 'bg-blue-500/20 border-blue-500/50 text-blue-300',
  hardware: 'bg-gray-500/20 border-gray-500/50 text-gray-300',
  environment: 'bg-green-500/20 border-green-500/50 text-green-300',
  liveware_self: 'bg-amber-500/20 border-amber-500/50 text-amber-300',
  liveware_others: 'bg-purple-500/20 border-purple-500/50 text-purple-300'
};

export function FishboneEditor({ data, categories, onChange, token, onAISuggestApply }) {
  const modelType = data?.model_type || '6M';
  const causes = data?.causes || [];
  const categoryList = categories?.[modelType] || [];
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  
  const updateCause = (index, field, value) => {
    const updatedCauses = [...causes];
    updatedCauses[index] = { ...updatedCauses[index], [field]: value };
    onChange({ ...data, causes: updatedCauses });
  };
  
  const addSubCause = (categoryIndex) => {
    const updatedCauses = [...causes];
    if (!updatedCauses[categoryIndex].sub_causes) {
      updatedCauses[categoryIndex].sub_causes = [];
    }
    updatedCauses[categoryIndex].sub_causes.push('');
    onChange({ ...data, causes: updatedCauses });
  };

  const requestAISuggestions = async () => {
    if (!data?.effect) {
      toast.error('Please enter the effect/problem first');
      return;
    }
    
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/investigations/ai/suggest-fishbone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          effect: data.effect,
          model_type: modelType,
          current_causes: causes.filter(c => c.cause),
          incident_context: null
        })
      });
      
      if (res.ok) {
        const suggestions = await res.json();
        if (suggestions.error) {
          toast.error(suggestions.error);
        } else {
          setAiSuggestions(suggestions);
          toast.success('AI suggestions generated');
        }
      } else {
        toast.error('Failed to get AI suggestions');
      }
    } catch (error) {
      toast.error('Error getting AI suggestions');
    } finally {
      setAiLoading(false);
    }
  };

  const applyAISuggestions = () => {
    if (!aiSuggestions?.suggested_causes) return;
    
    const newCauses = aiSuggestions.suggested_causes.map(s => ({
      category: s.category,
      cause: s.cause,
      sub_causes: s.sub_causes || [],
      is_contributing: s.is_contributing !== false
    }));
    
    onChange({ ...data, causes: newCauses });
    
    if (onAISuggestApply) {
      onAISuggestApply(aiSuggestions);
    }
    
    setAiSuggestions(null);
    toast.success('AI suggestions applied');
  };

  return (
    <div className="space-y-4">
      {/* AI Assistant Banner */}
      <Card className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 border-purple-500/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/30">
                <Brain className="w-5 h-5 text-purple-300" />
              </div>
              <div>
                <h4 className="font-medium text-white">AI Fishbone Assistant</h4>
                <p className="text-sm text-slate-400">GPT-5.2 powered cause identification</p>
              </div>
            </div>
            <Button
              onClick={requestAISuggestions}
              disabled={aiLoading || !data?.effect}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {aiLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Get AI Suggestions</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* AI Suggestions Panel */}
      {aiSuggestions && (
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-purple-300 text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5" /> AI Suggested Causes
                <Badge className="bg-purple-500/30 text-purple-300">{aiSuggestions.confidence}</Badge>
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setAiSuggestions(null)}>Dismiss</Button>
                <Button size="sm" onClick={applyAISuggestions} className="bg-purple-600 hover:bg-purple-700">
                  Apply All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {aiSuggestions.suggested_causes?.map((s, idx) => (
                <div key={idx} className="p-2 rounded bg-slate-900/50">
                  <p className="text-xs text-purple-300 capitalize">{s.category?.replace('_', ' ')}</p>
                  <p className="text-sm text-slate-300">{s.cause}</p>
                </div>
              ))}
            </div>
            {aiSuggestions.root_causes_identified?.length > 0 && (
              <div className="mt-3 p-2 rounded bg-red-500/10 border border-red-500/30">
                <p className="text-xs text-red-300 font-medium">Root Causes:</p>
                <ul className="text-sm text-slate-300">
                  {aiSuggestions.root_causes_identified.map((rc, i) => (
                    <li key={i}>• {rc}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-lg">Effect (Problem)</CardTitle>
            <Select
              value={modelType}
              onValueChange={(v) => onChange({ ...data, model_type: v })}
            >
              <SelectTrigger className="w-32 bg-slate-900/50 border-slate-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="6M">6M Model</SelectItem>
                <SelectItem value="SHELL">SHELL Model</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-red-500/20">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
            <Input
              value={data?.effect || ''}
              onChange={(e) => onChange({ ...data, effect: e.target.value })}
              placeholder="What is the effect/problem?"
              className="bg-slate-900/50 border-slate-600 text-lg"
            />
          </div>
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categoryList.map(cat => {
          const catCauses = causes.filter(c => c.category === cat.key);
          const colorClass = FISHBONE_COLORS[cat.key] || 'bg-slate-500/20 border-slate-500/50 text-slate-300';
          
          return (
            <Card key={cat.key} className={`${colorClass} border`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">{cat.name}</CardTitle>
                <p className="text-xs opacity-70">{cat.description}</p>
              </CardHeader>
              <CardContent className="space-y-2">
                {catCauses.map((cause, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Input
                        value={cause.cause}
                        onChange={(e) => {
                          const causeIdx = causes.findIndex(c => c === cause);
                          updateCause(causeIdx, 'cause', e.target.value);
                        }}
                        placeholder="Cause..."
                        className="bg-slate-900/50 border-slate-600 text-sm h-8"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          onChange({
                            ...data,
                            causes: causes.filter(c => c !== cause)
                          });
                        }}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                    {cause.sub_causes?.map((sub, subIdx) => (
                      <div key={subIdx} className="ml-4 flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-current opacity-50" />
                        <Input
                          value={sub}
                          onChange={(e) => {
                            const causeIdx = causes.findIndex(c => c === cause);
                            const updatedSubs = [...(cause.sub_causes || [])];
                            updatedSubs[subIdx] = e.target.value;
                            updateCause(causeIdx, 'sub_causes', updatedSubs);
                          }}
                          placeholder="Sub-cause..."
                          className="bg-slate-900/50 border-slate-600 text-xs h-7"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => {
                            const causeIdx = causes.findIndex(c => c === cause);
                            const updated = cause.sub_causes.filter((_, i) => i !== subIdx);
                            updateCause(causeIdx, 'sub_causes', updated);
                          }}
                        >
                          <X className="w-3 h-3 text-slate-400" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ))}
                
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => {
                    const idx = causes.findIndex(c => c.category === cat.key);
                    if (idx >= 0) {
                      addSubCause(idx);
                    } else {
                      onChange({
                        ...data,
                        causes: [...causes, { category: cat.key, cause: '', sub_causes: [''] }]
                      });
                    }
                  }}
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Sub-cause
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
